import { formatUnits, parseUnits } from 'ethers/lib/utils';
import { formatUSD } from '../../helpers';
import Button from '../Button';

type PercentageSelectorBaseProps = {
    percentages?: number[];
    currentValue: string;
    decimals?: number;
    disableButtons?: boolean;
};

type WithCurrentValue = PercentageSelectorBaseProps & {
    totalValue: string;
    handleChange: (value: string) => void;
    onPercentageClick?: never;
    isActive?: never;
};

type WithoutCurrentValue = PercentageSelectorBaseProps & {
    totalValue?: never;
    handleChange?: never;
    onPercentageClick?: (percentage: number) => void;
    isActive: (percentage: number) => boolean;
};

type PercentageSelectorProps = WithCurrentValue | WithoutCurrentValue;

const PercentageSelector: React.FC<PercentageSelectorProps> = ({
    percentages = [25, 50, 75, 100],
    currentValue,
    totalValue,
    decimals,
    disableButtons = false,
    handleChange,
    onPercentageClick,
    isActive,
}) => {
    const handleClick = (percentage: number) => {
        const value = formatUnits(
            parseUnits(totalValue!, decimals).mul(percentage).div(100),
            decimals
        );

        handleChange!(
            formatUSD(value, {
                currency: '',
                maxDecimals: 18,
                useGrouping: false,
            })
        );
    };

    return (
        <div className='percentage-selector'>
            {percentages.map((percentage, index) => {
                const active = isActive
                    ? isActive(percentage)
                    : !parseUnits(totalValue).isZero() &&
                      parseUnits(totalValue!, decimals)
                          .mul(percentage)
                          .div(100)
                          .eq(parseUnits(currentValue || '0', decimals));

                return (
                    <Button
                        key={index}
                        className={active ? 'active' : ''}
                        onClick={() =>
                            onPercentageClick
                                ? onPercentageClick(percentage)
                                : handleClick(percentage)
                        }
                        tabIndex={active ? -1 : 0}
                        disabled={active || disableButtons}
                    >
                        {percentage}%
                    </Button>
                );
            })}
        </div>
    );
};

export default PercentageSelector;
